import { Autocomplete, Box, Grid, TextField } from '@mui/material';
import React, { FC, useEffect } from 'react'
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from 'react-hook-form';
import { monitoringAndEvaluationSchema } from '../../../Common/EEA2/MonitoringAndEvaluationCommon';
import { EEA2Service } from '../../../../Component/Api/EmploymentEquity/WorkforceProfile_EEA2/EEA2Service';
import { toast } from 'react-toastify';
import { AppQuill } from '../../../../Component/Common/Components/FormValidationInputs/AppQuill';
import { MDTypography } from '../../../../Component/Common/Components/MDTypography';
import { MonitoringAndEvaluationDto } from '../../../../Component/Model/Dto/EmploymentEquity/WorkforceProfile_EEA2/MonitoringAndEvaluation';
import { UserActivityService } from '../../../../Component/Api/UserActivity/UserActivityService';
import { MonitoringAndEvaluationSection } from '../../../../Component/Common/Constants/UserActivityTrackingSections';
import { UserActivityActionType } from '../../../../Component/Model/Enum/BEE123Enums';
interface IProps {
    scorecardId: string;
}
interface IYesOrNoOptions {
    label: string;
    value?: boolean;
}
interface IMonitoringPeriodOptions {
    label: string;
    value?: number;
}

const yesOrNoOptions: IYesOrNoOptions[] = [
    { label: "No", value: false },
    { label: "Yes", value: true },
]

const monitoringPeriodOptions: IMonitoringPeriodOptions[] = [
    { label: "Monthly", value: 1 },
    { label: "Quarterly", value: 2 },
]

type Inputform = {
    isConsultativeBody?: IYesOrNoOptions;
    isRegisteredTradeUnion?: IYesOrNoOptions;
    isEmployees?: IYesOrNoOptions;

    progressPeriod?: IMonitoringPeriodOptions;
    didAchieveAnnualObjectives?: IYesOrNoOptions;

    detail?: string;
};

export const MonitoringAndEvaluation: FC<IProps> = (props) => {
    const maxDetailLength = 600;

    const { control, reset, getValues } = useForm<Inputform>({
        mode: 'all',
        resolver: yupResolver(monitoringAndEvaluationSchema)
    });

    const getMonitoringAndEvaluationData = async (scorecardId: string) => {
        try {
            const dataResponse = await EEA2Service.GetMonitoringAndEvaluationInfo(scorecardId);
            const formData: Inputform = {
                isConsultativeBody: yesOrNoOptions.find(x => x.value === dataResponse.data.isConsultativeBody),
                isEmployees: yesOrNoOptions.find(x => x.value === dataResponse.data.isEmployees),
                isRegisteredTradeUnion: yesOrNoOptions.find(x => x.value === dataResponse.data.isRegisteredTradeUnion),

                progressPeriod: monitoringPeriodOptions.find(x => x.value === dataResponse.data.progressPeriodId),
                didAchieveAnnualObjectives: yesOrNoOptions.find(x => x.value === dataResponse.data.didAchieveAnnualObjectives),
                detail: dataResponse.data.detail
            }
            reset(formData);
        } catch (error: any) {
            console.log(error);
        }
    }

    const saveMonitoringAndEvaluationForm = async () => {
        const formData = getValues();
        monitoringAndEvaluationSchema.validate(formData, { abortEarly: false }).then(async() => {
            const requestData: MonitoringAndEvaluationDto.Dto = {
                scorecardId: props.scorecardId,
                detail: formData.detail,
                isConsultativeBody: formData.isConsultativeBody?.value,
                isEmployees: formData.isEmployees?.value,
                isRegisteredTradeUnion: formData.isRegisteredTradeUnion?.value,
    
                progressPeriodId: formData.progressPeriod?.value,
                didAchieveAnnualObjectives: formData.didAchieveAnnualObjectives?.value
            }
            const response = await EEA2Service.SaveMonitoringAndEvaluationInfo(requestData);
        }).catch(function (error: any) {
            toast.error(error.message);
        });
    }

    useEffect(() => {
        getMonitoringAndEvaluationData(props.scorecardId);

        return () => {
            if (localStorage.getItem('coreToken') !== null) {
                saveMonitoringAndEvaluationForm();
                UserActivityService.createActivityLog({
                    section: MonitoringAndEvaluationSection,
                    actionType: UserActivityActionType.Update,
                    actionDescription: "Update monitoring and evaluation",
                    scorecardId: props.scorecardId,
                    requestUrl: window.location.href
                });
            }
        }
    }, [props.scorecardId]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Box component="form" mt={2}>
            {/* Consultation */}
            <Box mb={5}>
                <MDTypography color='primary' variant='h6' fontWeight='bold' sx={{ mb: 1 }} >
                    Consultation
                </MDTypography>
                <Grid container mb={1}
                    alignItems="center"
                    direction="row"
                    justifyContent="flex-start">
                    <Grid item xs={10}>
                        <MDTypography variant='h7' color='secondary'>
                            Consultative body or employment equity forum
                        </MDTypography>
                    </Grid>
                    <Grid item xs={2}>
                        <Controller
                            control={control}
                            name="isConsultativeBody"
                            defaultValue={{ label: "", value: undefined }}
                            render={({ field: { onChange, value } }) => (
                                <Autocomplete
                                    options={yesOrNoOptions}
                                    value={value}
                                    size="small"
                                    getOptionLabel={option => option.label}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    onChange={(_, data) => {
                                        onChange(data);
                                    }}
                                    renderInput={
                                        (params) => <TextField variant="outlined" {...params}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    }
                                />
                            )}
                        />
                    </Grid>
                </Grid>
                <Grid container mb={1}
                    alignItems="center"
                    direction="row"
                    justifyContent="flex-start">
                    <Grid item xs={10}>
                        <MDTypography variant='h7' color='secondary'>
                            Registered trade union(s)
                        </MDTypography>
                    </Grid>
                    <Grid item xs={2}>
                        <Controller
                            control={control}
                            name="isRegisteredTradeUnion"
                            defaultValue={{ label: "", value: undefined }}
                            render={({ field: { onChange, value } }) => (
                                <Autocomplete
                                    options={yesOrNoOptions}
                                    value={value}
                                    size="small"
                                    getOptionLabel={option => option.label}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    onChange={(_, data) => {
                                        onChange(data);
                                    }}
                                    renderInput={
                                        (params) => <TextField variant="outlined" {...params}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    }
                                />
                            )}
                        />
                    </Grid>
                </Grid>
                <Grid container mb={1}
                    alignItems="center"
                    direction="row"
                    justifyContent="flex-start">
                    <Grid item xs={10}>
                        <MDTypography variant='h7' color='secondary'>
                            Employees
                        </MDTypography>
                    </Grid>
                    <Grid item xs={2}>
                        <Controller
                            control={control}
                            name="isEmployees"
                            defaultValue={{ label: "", value: undefined }}
                            render={({ field: { onChange, value } }) => (
                                <Autocomplete
                                    options={yesOrNoOptions}
                                    value={value}
                                    size="small"
                                    getOptionLabel={option => option.label}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    onChange={(_, data) => {
                                        onChange(data);
                                    }}
                                    renderInput={
                                        (params) => <TextField variant="outlined" {...params}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    }
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </Box>
            {/* Monitoring and evaluation of implementation */}
            <Box mb={5}>
                <MDTypography color='primary' fontWeight='bold' sx={{ mb: 1 }} >
                    Monitoring and evaluation of implementation
                </MDTypography>
                <Grid container mb={1}
                    alignItems="center"
                    direction="row"
                    justifyContent="flex-start">
                    <Grid item xs={10}>
                        <MDTypography variant='h7' color='secondary'>How regularly do you monitor progress on the implementation of the Employment Equity Plan?</MDTypography>
                    </Grid>
                    <Grid item xs={2}>
                        <Controller
                            control={control}
                            name="progressPeriod"
                            defaultValue={{ label: "", value: undefined }}
                            render={({ field: { onChange, value } }) => (
                                <Autocomplete
                                    options={monitoringPeriodOptions}
                                    value={value}
                                    size="small"
                                    getOptionLabel={option => option.label}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    onChange={(_, data) => {
                                        onChange(data);
                                    }}
                                    renderInput={
                                        (params) => <TextField variant="outlined" {...params}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    }
                                />
                            )}
                        />
                    </Grid>
                </Grid>
                <Grid container mb={1}
                    alignItems="center"
                    direction="row"
                    justifyContent="flex-start">
                    <Grid item xs={10}>
                        <MDTypography variant='h7' color='secondary'>Did you achieve the annual objectives as set out in your Employment Equity Plan for this period?</MDTypography>
                    </Grid>
                    <Grid item xs={2}>
                        <Controller
                            control={control}
                            name="didAchieveAnnualObjectives"
                            defaultValue={{ label: "", value: undefined }}
                            render={({ field: { onChange, value } }) => (
                                <Autocomplete
                                    options={yesOrNoOptions}
                                    value={value}
                                    size="small"
                                    getOptionLabel={option => option.label}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    onChange={(_, data) => {
                                        onChange(data);
                                    }}
                                    renderInput={
                                        (params) => <TextField variant="outlined" {...params}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    }
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </Box>
            {/* Quill */}
            <Box>
                <AppQuill control={control} name='detail' label='Detail' maxLength={maxDetailLength} />
            </Box>
        </Box>
    )
}
